import { useEffect, useState } from 'react';
import { useGetStorehouses } from 'api/counterparties';
import {
  GetStorehousesParams,
  Storehouse,
} from 'api/counterparties/counterparties.types';
import { AutocompletePaginationAsync } from 'components/Inputs';
import { FieldValues } from 'react-hook-form';
import { createHandleOnBlur, handleChange } from 'utils/form.utils';

export default function LoadingDeliveryLocationAutocomplete<
  TFieldValues extends FieldValues = FieldValues,
>({
  formProps,
  error,
  customerId,
}: {
  formProps: TFieldValues;
  error: boolean;
  customerId?: number | null;
}) {
  const { data, loading, getStorehouses } = useGetStorehouses();
  const [options, setOptions] = useState<Storehouse[]>([]);
  const [storehouseParams, setStorehouseParams] =
    useState<GetStorehousesParams>({
      filterText: '',
      resultSize: 25,
      pageNumber: 1,
    });

  const isDisabled = !customerId;

  useEffect(() => {
    if (customerId) {
      const getStorehousesForCustomer = getStorehouses(customerId);
      getStorehousesForCustomer(storehouseParams);
    }
  }, [getStorehouses, customerId, storehouseParams]);

  useEffect(() => {
    if (data?.length) {
      const filteredOptions = data
        .filter((storehouse) => storehouse.enabled)
        .map((storehouse) => ({
          ...storehouse,
        }));
      setOptions((prev) => {
        const map = new Map(prev.map((s) => [s.id, s]));

        filteredOptions.forEach((storehouse) => {
          map.set(storehouse.id, storehouse);
        });

        return Array.from(map.values());
      });
    }
  }, [data]);

  useEffect(() => {
    if (!customerId) {
      setOptions([]);
      formProps.field.onChange(null);
      setStorehouseParams({
        filterText: '',
        resultSize: 25,
        pageNumber: 1,
      });
    }
  }, [customerId, formProps.field]);

  return (
    <AutocompletePaginationAsync
      {...formProps}
      padding='dense'
      width={280}
      value={formProps.field.value}
      error={error}
      loading={loading}
      disabled={isDisabled}
      options={options}
      isOptionEqualToValue={(option, value) => value?.id === option?.id}
      getOptionLabel={(o) => `${o.code}`}
      handleSearchChange={handleChange(setOptions, setStorehouseParams)}
      onChange={(_, data) => formProps.field.onChange(data)}
      onScrollBottom={() =>
        setStorehouseParams((prev: any) => ({
          ...prev,
          pageNumber: prev.pageNumber + 1,
        }))
      }
      onBlur={createHandleOnBlur(
        setOptions,
        setStorehouseParams,
        formProps.field.onBlur,
      )}
    />
  );
}
